/**
 * Unpublish + draft-from-version (rollback) request and response schemas
 * (issue #13).
 *
 * The lifecycle endpoints operate on `ContentVersion` and `PageVersion` rows
 * after the publish workflow (#12) has run:
 *
 *   POST /api/admin/content-versions/:id/unpublish
 *   POST /api/admin/page-versions/:id/unpublish
 *   POST /api/admin/content-items/:id/versions/draft-from/:sourceVersionId
 *   POST /api/admin/pages/:id/versions/draft-from/:sourceVersionId
 *
 * Spec §7 Unpublishing, §7 Amending Published Content, §7 Rollback.
 *
 * Slice boundary:
 *   - Issue #12 (publish) owns `publish.ts`. Unpublish + draft-from share this
 *     file so #13 never touches `publish.ts` (avoids merge conflict with any
 *     follow-on publish tweaks).
 *   - There is no separate `/rollback` endpoint by design. Rollback is the
 *     workflow `draft-from(ARCHIVED) → publish` — two existing endpoints
 *     composed by the operator. Documenting the mechanism here so a future
 *     reader doesn't accidentally re-introduce it.
 *
 * Body shape rationale:
 *   - All four endpoints take an empty body today. We still parse with
 *     `z.object({}).strict()` so any future extension (e.g. a comment or a
 *     reason code on unpublish) lands without a controller change, AND
 *     because `.strict()` rejects unknown top-level keys — a typo like
 *     `{ overrideFlags: true }` on unpublish would otherwise be silently
 *     swallowed and produce a confused operator.
 */

import { z } from "zod";

/**
 * Body schema for `POST /api/admin/content-versions/:id/unpublish`. Empty
 * body — the action is fully described by the URL. `.strict()` rejects
 * unknown keys for typo detection.
 */
export const unpublishContentVersionSchema = z.object({}).strict();
export type UnpublishContentVersionInput = z.infer<typeof unpublishContentVersionSchema>;

/**
 * Body schema for `POST /api/admin/page-versions/:id/unpublish`. Empty body,
 * same rationale as the content variant.
 */
export const unpublishPageVersionSchema = z.object({}).strict();
export type UnpublishPageVersionInput = z.infer<typeof unpublishPageVersionSchema>;

/**
 * Body schema for `POST /api/admin/content-items/:id/versions/draft-from/:sourceVersionId`.
 * Empty body — the source version is identified by the path param. The
 * service copies `contentJson`, `styleJson`, and `aiMetaJson` byte-for-byte
 * from the source into the new draft. `.strict()` rejects unknowns.
 */
export const draftFromContentVersionSchema = z.object({}).strict();
export type DraftFromContentVersionInput = z.infer<typeof draftFromContentVersionSchema>;

/**
 * Body schema for `POST /api/admin/pages/:id/versions/draft-from/:sourceVersionId`.
 * Empty body. The service copies `title`, `seoTitle`, `seoDescription`,
 * `ogImageId`, `settingsJson`, and `blocksOrderJson` from the source into
 * the new draft.
 */
export const draftFromPageVersionSchema = z.object({}).strict();
export type DraftFromPageVersionInput = z.infer<typeof draftFromPageVersionSchema>;

/**
 * Response shape for `POST /api/admin/content-versions/:id/unpublish`. The
 * action transitions the target version from PUBLISHED to ARCHIVED with no
 * replacement, so we surface the archived version's id and the recorded
 * `PublishEvent` id so the SPA can render the activity log entry without a
 * follow-up GET.
 */
export type UnpublishContentVersionResponse = {
  archivedVersionId: string;
  publishEventId: string;
};

/**
 * Response shape for `POST /api/admin/page-versions/:id/unpublish`. Same
 * layout as the content variant — the SPA uses one renderer for both.
 */
export type UnpublishPageVersionResponse = {
  archivedVersionId: string;
  publishEventId: string;
};
